import { useState, useEffect, useCallback, useMemo } from 'react';
import { dashboardApi, LatestOfferResponse } from '../api/dashboard.api';
import {
  CompletedContainer,
  PortDepartures,
  PortTransitTime,
  CurrentPrice,
} from '../interfaces/mock.interface';

interface DashboardData {
  completedContainers: CompletedContainer[];
  vesselDepartures: PortDepartures[];
  portTransitTimes: PortTransitTime[];
  currentPrices: CurrentPrice[];
  latestOffer: LatestOfferResponse | null;
}

interface UseDashboardDataReturn {
  data: DashboardData;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
}

const defaultData: DashboardData = {
  completedContainers: [],
  vesselDepartures: [],
  portTransitTimes: [],
  currentPrices: [],
  latestOffer: null,
};

/**
 * Custom hook for fetching and managing dashboard data
 * @param autoRefreshInterval - Interval in milliseconds for auto-refresh (default: 5 minutes, 0 to disable)
 */
export const useDashboardData = (autoRefreshInterval: number = 5 * 60 * 1000): UseDashboardDataReturn => {
  const [data, setData] = useState<DashboardData>(defaultData);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const api = useMemo(() => dashboardApi, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await api.getAllDashboardData();
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err : new Error('A apărut o eroare la încărcarea datelor'));
    } finally {
      setLoading(false);
    }
  }, [api]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Initial fetch on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchData();
    }, autoRefreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [autoRefreshInterval, fetchData]);

  return {
    data,
    loading,
    error,
    refresh,
    lastUpdated,
  };
};

export default useDashboardData;
